/**
 * Secure a string to be used as an identifier
 * @param str {string} String to secure
 * @returns {string} Secured string, trimmed, normalized and without accents
 */
export function secureString(str) {
    return String(str).trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

/**
 * Search for a string in another string
 * @param string {string} String to search in
 * @param terms {string} String to search for
 * @returns {boolean} True if the string contains the terms, false otherwise
 */
export function searchTerms(string = '', terms = '') {
    if (string === null || string === undefined) return false
    return secureString(string).includes(secureString(terms))
}

/**
 * Debounce a function
 * @param func {function(): *} Function to debounce
 * @param wait {number} Time to wait before calling the function
 * @param immediate {boolean} If true, the function is called immediately, otherwise it is called after the wait time
 * @param context {object} Context of the function
 * @returns {function(): *} Debounced function
 */
function debounce(func, wait, immediate = false, context = null) {
    let result;
    let timeout = null;
    return function() {
        const ctx = context || this, args = arguments;
        const later = function() {
            timeout = null;
            if (!immediate) result = func.apply(ctx, args);
        };
        const callNow = immediate && !timeout;
        // Tant que la fonction est appelée, on reset le timeout.
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) result = func.apply(ctx, args);
        return result;
    };
}

/**
 * Bind data to an element
 * @param element {HTMLElement} Element to bind data to
 * @param as {string} Attribute to bind data to
 * @param data {string} Data to bind to the element
 */
function bindElementData(element, as, data) {
    if (as in element) element[as] = data;
    else element.setAttribute(as, data);
}

/**
 * The FilterForAttribute class provides functionality to filter a list of items
 * dynamically, either locally or remotely, based on user input or custom events.
 * It manages interaction with input elements, performs filtering logic, and
 * updates the associated list of items.
 */
class FilterForAttribute {
    #filterInput;
    #itemsWrapper;
    #listUpdateTasks;

    /**
     * Creates a new instance of FilterForAttribute.
     * @param filterInput {HTMLElement} The input element used for filtering.
     */
    constructor(filterInput) {
        this.#filterInput = filterInput;
    }

    /**
     * Initializes the filter for attribute by binding event listeners,
     * performing initial filtering, and updating the associated list of items.
     */
    init() {
        this.#itemsWrapper = document.getElementById(this.#filterInput.getAttribute('filterfor'));

        const debouncedFiltering = debounce(async () => {
            this.#itemsWrapper.dispatchEvent(new CustomEvent('beforefilter'));
            const terms = this.#filterInput.value.trim();
            const filterEndpoint = this.#filterInput.getAttribute('filterendpoint');
            if (filterEndpoint) {
                this.#listUpdateTasks = await this.remoteFilter(terms, filterEndpoint);
                this.execListUpdateTasks();
            } else {
                this.localFilter(terms);
            }
            this.#itemsWrapper.dispatchEvent(new CustomEvent('afterfilter'));
        }, 300);

        this.#filterInput.addEventListener('input', debouncedFiltering);
        this.#filterInput.addEventListener('applyfilter', debouncedFiltering);
    }

    /**
     * Performs local filtering of the associated list of items.
     * @param terms {string} The search terms to filter by.
     */
    localFilter(terms) {
        for (const item of this.#itemsWrapper?.children ?? [ ]) {
            const text = item.dataset?.filterdata ?? item.textContent;
            item.style.display = !terms || searchTerms(text, terms) ? null : 'none';
        }
    }

    /**
     * Executes all pending list update tasks.
     * This method is called after filtering is complete to update the list of items.
     * It clears the previous list of items, executes all pending tasks, and updates the list.
     */
    execListUpdateTasks() {
        this.#itemsWrapper.innerHTML = '';
        for (const task of this.#listUpdateTasks ?? [ ]) {
            task();
        }
        this.#listUpdateTasks = null;
    }

    /**
     * Performs remote filtering of the associated list of items.
     * This method fetches data from a remote endpoint and updates the list of items accordingly.
     * It first clears the previous list of items, then fetches data from the remote endpoint,
     * parses the response, and updates the list of items accordingly.
     * @param terms {string} The search terms to filter by.
     * @param filterEndpoint {string} The URL of the remote endpoint to fetch data from.
     * @returns {Promise<*[]>} A promise that resolves to an array of tasks to update the list of items.
     */
    async remoteFilter(terms, filterEndpoint) {
        this.#itemsWrapper.innerHTML = '';

        const [ filteredItems ] = await Promise.all([
            new Promise(async resolve => {
                const response = await fetch(filterEndpoint.replace(/%s/gi, encodeURIComponent(terms)));
                const filteredItems = await response.json();
                resolve(filteredItems);
            }),
            new Promise(resolve => setTimeout(resolve, 500))
        ]);

        let index = 0;
        const templateId = this.#filterInput.getAttribute('filteritemtemplate');
        const templateContent = document.getElementById(templateId).content;

        const tasks = [ ];
        for (const item of filteredItems) {
            const template = templateContent.cloneNode(true);
            const root = template.querySelector('.js-item');
            root.dataset.id = (root.dataset.id ?? '').replace(/%i/gi, String(index++));
            const idElements = root.querySelectorAll('[data-id-as]');
            const labelElements = root.querySelectorAll('[data-label-as]');
            const valueElements = root.querySelectorAll('[data-value-as]');
            for (const idElement of idElements) {
                bindElementData(idElement, idElement.dataset.idAs, root.dataset.id);
            }
            for (const labelElement of labelElements) {
                bindElementData(labelElement, labelElement.dataset.labelAs, item.label);
            }
            for (const valueElement of valueElements) {
                bindElementData(valueElement, valueElement.dataset.valueAs, item.value);
            }
            tasks.push(() => this.#itemsWrapper.appendChild(root));
        }

        return tasks;
    }
}

/**** INIT ****/

const filtersInputs = document.querySelectorAll('[filterfor]');

for (const filterInput of filtersInputs) {
    const instance = new FilterForAttribute(filterInput);
    instance.init();
}